// Zoom-style VIDEO transport: self-encode the camera with WebCodecs and stream
// INDIVIDUAL encoded H.264 frames over a per-source WebSocket — no WebRTC (no
// ICE/DTLS/SDP renegotiation) and no MediaRecorder (no container fragments). Every
// frame carries a capture timestamp; a keyframe is forced on every (re)connect so a
// dropped socket resumes instantly without touching the capture — the encoder and the
// getUserMedia track stay alive, only the transport is replaced.
//
// This project records NO audio: there is no mic capture and no AudioEncoder.
//
// Two frame sources are supported so the capture works beyond Chromium:
//   - MediaStreamTrackProcessor  — Chrome/Edge desktop and Android (pulls VideoFrames
//     straight off the MediaStreamTrack).
//   - requestVideoFrameCallback  — WebKit / iOS Safari 16.4+, where VideoEncoder and
//     VideoFrame exist but MediaStreamTrackProcessor does NOT: a hidden <video> plays
//     the track and each presented frame is drawn to a canvas and wrapped in a
//     VideoFrame for the SAME encoder.
// Callers MUST gate on isCaptureSupported().
//
// Wire format — one binary WebSocket message per frame (little-endian), UNCHANGED from
// the A/V era so the server ingest stays compatible (audio fields are always 0 now):
//   byte  0     : version (1)
//   byte  1     : flags   (bit0 = keyframe)
//   byte  2     : codec   (CODEC_H264)
//   byte  3     : channels (0 — video)
//   bytes 4..11 : capture timestamp, microseconds (float64)
//   bytes 12..15: duration, microseconds (uint32)
//   bytes 16..19: payload length (uint32)
//   bytes 20..23: sample rate, Hz (0 — video) (uint32)
//   bytes 24.. : payload (H.264 Annex-B access unit)

export const HEADER_BYTES = 24;
export const PROTO_VERSION = 1;
export const CODEC_H264 = 0;

// H.264 Baseline codec string with the LOWEST level whose max frame size covers
// w×h — a fixed low level (e.g. 3.1 = avc1.42E01F, max 1280×720) makes VideoEncoder
// reject a 1080p screen. Level byte is the last hex pair: 3.1=1F, 4.0=28, 5.0=32,
// 5.1=33. Frame is measured in 16×16 macroblocks (MaxFS per H.264 Annex A).
function avcCodec(w, h) {
  const mbs = Math.ceil(w / 16) * Math.ceil(h / 16);
  let level = 0x1f;                 // 3.1  (MaxFS 3600)
  if (mbs > 3600) level = 0x28;     // 4.0  (MaxFS 8192, covers 1080p)
  if (mbs > 8192) level = 0x32;     // 5.0  (MaxFS 22080)
  if (mbs > 22080) level = 0x33;    // 5.1  (MaxFS 36864, covers 4K)
  return `avc1.42E0${level.toString(16).toUpperCase().padStart(2, "0")}`;
}

// True when the browser can self-encode H.264 with WebCodecs AND has a way to pull
// camera frames into the encoder (MediaStreamTrackProcessor on Chromium, or
// requestVideoFrameCallback on WebKit/iOS Safari 16.4+). No audio is required.
export function isCaptureSupported() {
  if (typeof window === "undefined") return false;
  const hasEncoder = typeof window.VideoEncoder === "function"
    && typeof window.EncodedVideoChunk === "function"
    && typeof window.VideoFrame === "function";
  if (!hasEncoder) return false;
  const hasTrackProcessor = typeof window.MediaStreamTrackProcessor === "function";
  const hasRvfc = typeof window.HTMLVideoElement === "function"
    && typeof window.HTMLVideoElement.prototype.requestVideoFrameCallback === "function";
  return hasTrackProcessor || hasRvfc;
}

function frameMessage(keyframe, tsMicros, durMicros, payload) {
  const buf = new ArrayBuffer(HEADER_BYTES + payload.byteLength);
  const dv = new DataView(buf);
  dv.setUint8(0, PROTO_VERSION);
  dv.setUint8(1, keyframe ? 1 : 0);
  dv.setUint8(2, CODEC_H264);
  dv.setUint8(3, 0);                 // channels: video
  dv.setFloat64(4, tsMicros, true);
  dv.setUint32(12, Math.max(0, durMicros | 0), true);
  dv.setUint32(16, payload.byteLength, true);
  dv.setUint32(20, 0, true);         // sample rate: video
  new Uint8Array(buf, HEADER_BYTES).set(payload);
  return buf;
}

// A single camera's encode+send pipeline. Owns a WebSocket (via the openWs factory,
// which must return a fresh WebSocket each call so a drop can reconnect with a new
// token) but NOT the MediaStreamTrack — the track is owned by the capture page and
// outlives any number of reconnects.
export class SourceSender {
  // openWs: () => WebSocket. fps/bitrate for the H.264 encode.
  constructor({ track, openWs, fps = 15, bitrate = 2_500_000, onState, label }) {
    this.track = track;
    this.label = label || "video";
    this.openWs = openWs;
    this.fps = fps > 0 ? fps : 15;
    this.bitrate = bitrate;
    this.onState = onState || (() => {});
    this._sent = 0;             // frames sent since last stats tick
    this._enc = 0;              // encode() calls since last stats tick
    this._statsTimer = null;
    this._encTimer = null;      // constant-fps keepalive timer
    this._heldFrame = null;     // newest VideoFrame awaiting encode
    this._rvfcVideo = null;     // hidden <video> driving requestVideoFrameCallback
    this._rvfcCanvas = null;
    this._rvfcHandle = null;
    this.ws = null;
    this.encoder = null;
    this.processor = null;
    this.reader = null;
    this.stopped = false;
    this.connected = false;        // socket currently open (for the reconnecting UI)
    this.needKeyframe = true;      // force a keyframe on the first frame after (re)connect
    this.reconnectTimer = null;
    this.backoff = 500;
    this.t0 = null;                // capture-clock anchor (first frame), microseconds base
  }

  start() {
    this.stopped = false;
    this._connect();
    this._startVideo();
    // Periodic stats so a stall/backpressure is visible in server telemetry.
    this._statsTimer = setInterval(() => {
      const buf = this.ws ? this.ws.bufferedAmount : -1;
      console.debug(`[wc] ${this.label} sent=${this._sent}/5s enc=${this._enc} buffered=${buf} connected=${this.connected}`);
      this._sent = 0; this._enc = 0;
    }, 5000);
    return this;
  }

  stop() {
    this.stopped = true;
    this.connected = false;
    if (this._statsTimer) { clearInterval(this._statsTimer); this._statsTimer = null; }
    if (this._encTimer) { clearInterval(this._encTimer); this._encTimer = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    // requestVideoFrameCallback source (iOS Safari): stop the callback + release the video.
    try { if (this._rvfcVideo && this._rvfcHandle) this._rvfcVideo.cancelVideoFrameCallback(this._rvfcHandle); } catch { /* noop */ }
    this._rvfcHandle = null;
    try { if (this._rvfcVideo) { this._rvfcVideo.pause(); this._rvfcVideo.srcObject = null; this._rvfcVideo.remove(); } } catch { /* noop */ }
    this._rvfcVideo = null;
    this._rvfcCanvas = null;
    // MediaStreamTrackProcessor source (Chromium).
    try { this.reader?.cancel(); } catch { /* noop */ }
    this.reader = null;
    this.processor = null;
    try { this._heldFrame?.close(); } catch { /* noop */ }
    this._heldFrame = null;
    try { if (this.encoder && this.encoder.state !== "closed") this.encoder.close(); } catch { /* noop */ }
    this.encoder = null;
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = null;
    // We own a CLONE of the source track (the page keeps the original for its
    // preview), so stop the clone to release it.
    try { this.track?.stop(); } catch { /* noop */ }
  }

  // Force a transport-only reconnect (watchdog silent reconnect): drop the socket;
  // onclose reopens it and the next frame is a keyframe. Capture/encoder untouched.
  reconnect() {
    try { this.ws?.close(); } catch { /* noop */ }
  }

  // Re-point the transport at a NEW source (e.g. a new phase bucket) WITHOUT tearing
  // down the encoder/track: swap the openWs factory and reconnect. The swap is flagged
  // so onState does NOT report a disconnect (a deliberate phase switch is not a drop).
  setSource(openWs) {
    this._swapping = true;
    this.openWs = openWs;
    try { this.ws?.close(); } catch { /* noop */ }   // onclose reopens via the new factory
  }

  _wsOpen() { return this.ws && this.ws.readyState === WebSocket.OPEN; }

  // --- Transport: reconnect keeps the encoder/capture alive; only the socket is new.
  _connect() {
    if (this.stopped) return;
    let ws;
    try { ws = this.openWs(); } catch { this._scheduleReconnect(); return; }
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      this.connected = true;
      this.needKeyframe = true;   // Zoom-style resume: next frame is a keyframe
      this._swapping = false;     // the intentional source-swap completed
      this.onState({ connected: true });
      console.info(`[wc] ${this.label} ws open`);
    };
    ws.onclose = (ev) => {
      this.connected = false;
      // A deliberate source swap (setSource) is not a drop — don't alarm the page.
      if (!this._swapping) this.onState({ connected: false });
      console.warn(`[wc] ${this.label} ws close code=${ev && ev.code} reason=${(ev && ev.reason) || ""}`);
      this._scheduleReconnect();
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  }

  _scheduleReconnect() {
    if (this.stopped || this.reconnectTimer) return;
    const jitter = Math.floor(Math.random() * 250);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connect();
    }, Math.min(this.backoff, 8000) + jitter);
    this.backoff = Math.min(this.backoff * 2, 8000);
  }

  _send(buf) {
    if (!this._wsOpen()) return;
    // Shed if the uplink can't drain (dying link) rather than buffering unbounded.
    if (this.ws.bufferedAmount > 8 * 1024 * 1024) {
      console.warn(`[wc] ${this.label} uplink backpressure (buffered=${this.ws.bufferedAmount}) → reset socket`);
      try { this.ws.close(); } catch { /* noop */ }
      return;
    }
    try { this.ws.send(buf); this._sent++; } catch { /* dropped; reconnect recovers */ }
  }

  _startVideo() {
    const enc = new window.VideoEncoder({
      output: (chunk) => {
        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);
        this._send(frameMessage(chunk.type === "key", chunk.timestamp, chunk.duration || 0, data));
      },
      error: (e) => {
        this.needKeyframe = true;   // encoder hiccup → force a fresh keyframe
        console.error(`[wc] ${this.label} encoder error: ${e && e.message ? e.message : e}`);
      },
    });
    this.encoder = enc;
    let last = -1, cw = 0, ch = 0, sinceKey = 0;
    let lastEncodeWallMs = 0;         // perf clock of the last encode (real or keepalive)
    const minGap = 1e6 / this.fps;    // cap the encode rate at the configured fps
    const frameDurUs = Math.round(1e6 / this.fps);
    const nowMs = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
    // Force a keyframe at least every ~2s (a GOP) so the server's H.264 producer —
    // which may restart mid-stream on a reconnect — always has a nearby recovery point.
    const keyInterval = Math.max(1, Math.round(this.fps * 2));
    // VideoEncoder.configure REQUIRES the frame dimensions, learned from the first
    // VideoFrame — so configure LAZILY (and reconfigure on a resize). H.264 Annex-B
    // keeps SPS/PPS inline so the server reads `-f h264` with no out-of-band config.
    const ensureConfigured = (w, h) => {
      w &= ~1; h &= ~1;   // H.264 needs even dimensions
      if (!w || !h) return false;
      if (enc.state === "configured" && w === cw && h === ch) return true;
      cw = w; ch = h;
      enc.configure({
        codec: avcCodec(w, h),
        avc: { format: "annexb" },
        width: w, height: h,
        bitrate: this.bitrate,
        framerate: this.fps,
        latencyMode: "realtime",
      });
      this.needKeyframe = true;   // first frame at a new config must be a keyframe
      console.info(`[wc] ${this.label} encoder configured ${w}x${h} @${this.fps}fps ${this.bitrate}bps`);
      return true;
    };
    // Encode one frame and run the keyframe/stat bookkeeping. `tsUs` is that frame's
    // timestamp, tracked so the keepalive can advance monotonically from it.
    const encodeOne = (frame, tsUs) => {
      const key = this.needKeyframe || sinceKey >= keyInterval;
      enc.encode(frame, { keyFrame: key });
      this.needKeyframe = false;
      sinceKey = key ? 0 : sinceKey + 1;
      last = tsUs;
      lastEncodeWallMs = nowMs();
      this._enc++;
    };
    // Ingest one source frame (shared by both frame sources): rate-cap to fps, encode,
    // then hold the NEWEST frame open for the keepalive re-encode below.
    const onFrame = (frame, tsUs) => {
      try {
        if (ensureConfigured(frame.codedWidth, frame.codedHeight)
            && enc.state === "configured" && this._wsOpen()
            && enc.encodeQueueSize < 4
            && (last < 0 || tsUs - last >= minGap)) {
          encodeOne(frame, tsUs);
        }
      } catch { this.needKeyframe = true; }
      const prev = this._heldFrame;
      this._heldFrame = frame;
      if (prev && prev !== frame) { try { prev.close(); } catch { /* noop */ } }
    };
    // KEEPALIVE (constant-fps pacing): if the source stalls (e.g. a backgrounded tab
    // throttles the reader), re-encode the last held frame on a timer so the uplink
    // never goes idle (the server closes an idle socket and flags a storage stall). The
    // held frame is cloned with a fresh, monotonically advancing timestamp.
    const frameIntervalMs = Math.max(1, Math.round(1000 / this.fps));
    this._encTimer = setInterval(() => {
      if (this.stopped || !this._wsOpen()) return;
      const held = this._heldFrame;
      if (!held) return;
      if (nowMs() - lastEncodeWallMs < frameIntervalMs * 0.9) return;   // only fill gaps
      if (!(ensureConfigured(held.codedWidth, held.codedHeight)
            && enc.state === "configured" && enc.encodeQueueSize < 4)) return;
      const tsUs = (last < 0 ? held.timestamp : last) + frameDurUs;
      let clone;
      try { clone = new window.VideoFrame(held, { timestamp: tsUs, duration: frameDurUs }); }
      catch { return; }
      try { encodeOne(clone, tsUs); }
      catch { this.needKeyframe = true; }
      finally { try { clone.close(); } catch { /* noop */ } }
    }, frameIntervalMs);

    if (typeof window.MediaStreamTrackProcessor === "function") {
      this._pumpTrackProcessor(onFrame);
    } else {
      this._pumpRvfc(onFrame);
    }
  }

  // Chromium frame source: pull VideoFrames straight off the track. Works in a
  // backgrounded tab (unlike a setInterval-only encoder throttled to ~1 Hz).
  _pumpTrackProcessor(onFrame) {
    const proc = new window.MediaStreamTrackProcessor({ track: this.track });
    this.processor = proc;
    const reader = proc.readable.getReader();
    this.reader = reader;
    const pump = async () => {
      while (!this.stopped) {
        let res;
        try { res = await reader.read(); } catch { break; }
        if (res.done) break;
        onFrame(res.value, res.value.timestamp);
      }
    };
    pump();
  }

  // WebKit / iOS Safari frame source: MediaStreamTrackProcessor doesn't exist, so play
  // the track in a hidden <video> and, on each presented frame (requestVideoFrameCallback),
  // draw it to a canvas and wrap that in a VideoFrame for the same encoder. Timestamps
  // come from a monotonic wall clock (mediaTime isn't reliable across engines).
  _pumpRvfc(onFrame) {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    try { video.srcObject = new MediaStream([this.track]); } catch { return; }
    // Keep it renderable but invisible — some WebKit builds won't decode a
    // display:none video (no frames → rVFC never fires).
    Object.assign(video.style, {
      position: "fixed", top: "0", left: "0", width: "1px", height: "1px",
      opacity: "0", pointerEvents: "none", zIndex: "-1",
    });
    document.body.appendChild(video);
    this._rvfcVideo = video;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { alpha: false, willReadFrequently: false });
    this._rvfcCanvas = canvas;
    const startMs = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const tick = () => {
      if (this.stopped) return;
      try {
        const w = video.videoWidth, h = video.videoHeight;
        if (w && h && this._wsOpen()) {
          if (canvas.width !== w) canvas.width = w;
          if (canvas.height !== h) canvas.height = h;
          ctx.drawImage(video, 0, 0, w, h);
          const tsUs = Math.round(((typeof performance !== "undefined" ? performance.now() : Date.now()) - startMs) * 1000);
          let frame = null;
          try { frame = new window.VideoFrame(canvas, { timestamp: tsUs, duration: Math.round(1e6 / this.fps) }); }
          catch { frame = null; }
          if (frame) onFrame(frame, tsUs);
        }
      } catch { /* skip this frame */ }
      if (!this.stopped && this._rvfcVideo) {
        this._rvfcHandle = this._rvfcVideo.requestVideoFrameCallback(tick);
      }
    };
    const begin = () => {
      if (this.stopped || !this._rvfcVideo) return;
      this._rvfcHandle = this._rvfcVideo.requestVideoFrameCallback(tick);
    };
    let p;
    try { p = video.play(); } catch { p = null; }
    if (p && typeof p.then === "function") p.then(begin).catch(begin);
    else begin();
  }
}
